import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicInput, NumberInput, SimplePopover, Typography } from '@dev/ee-components';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { clone, find } from 'lodash-es';
import { SplitOptimization } from '@dev/ee-api/ee-api-models';

import { getTimeInMinutes } from '@/services/tools/getTimeInMinutes';
import { TimeUnitsEnum } from '@/services/dictionaries/TimeUnits';
import { CampaignCreatorType } from '@/services/dictionaries/Paths';
import { WinnerSelectionTimeOptions } from '@/services/dictionaries/ScheduleTimeOptions';
import { getTimeInUnits } from '@/services/tools/getTimeInUnits';
import { CampaignErrorNames } from '@/store/campaignCreator/types';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import {
    getCampainCreatorStore,
    ModifyCampaignMap,
    SetCampaignErrors,
    SetCampaignErrorsSubscriber,
    VerifyCampaign,
} from '@/store/campaignCreator/actions';
import { useGetABWinnerInfo } from '@/components/CampaignCreator/hooks/useGetABWinnerInfo';
import { ErrorSubscriberOrder, SplitOptimizationEnum } from '../../../CampaignCreatorInterface';

const WinnerSelectionWrapper = styled.div`
    margin-top: 24px;
`;

const InputsWrapper = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    .inputWrapperBasicInput:nth-child(1) {
        max-width: 294px;
    }
    .inputWrapperBasicInput:nth-child(2) {
        max-width: 90px;

        .MuiInputBase-input {
            min-width: 30px;
        }
    }
    .inputWrapperBasicInput:nth-child(3) {
        max-width: 101px;
    }
`;

const getOptionLabel = (
    value: SplitOptimization | TimeUnitsEnum,
    options: { value: SplitOptimization | TimeUnitsEnum; label: string }[]
): string => {
    const label = find(options, (option) => option.value === value)?.label;
    if (!label) {
        return value;
    }
    return label;
};

export const WinnerSelection = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { campaign, errors } = useSelector(getCampainCreatorStore);
    const { value, unit } = getTimeInUnits(campaign?.SplitOptions?.DetermineWinnerAfterXMinutes) || {};
    const { determineWinnerUnit, determineWinnerValue } = useGetABWinnerInfo();
    const [splitOptimization, setSplitOptimization] = useState(
        campaign?.SplitOptions?.SplitOptimization || SplitOptimization.Opened
    );
    const { type } = useParams<{ name: string; type: string }>();
    const isAbCampaign = type === CampaignCreatorType.AB;
    const { isDetailsScreen } = useCampaignScreensPathsChecker();

    const id = 'step-Settings & tracking-3';
    const winnerSelectionErrorName = CampaignErrorNames.WINNER_SELECTION_UNIT;
    const hasWinnerSelectionError = !!errors?.[winnerSelectionErrorName];

    useEffect((): void => {
        dispatch(
            SetCampaignErrorsSubscriber({
                id,
                errorName: winnerSelectionErrorName,
                order: ErrorSubscriberOrder.SENDING_OPTIONS,
            })
        );
    }, []);

    useEffect((): void => {
        if (determineWinnerUnit === TimeUnitsEnum.Minutes && !isDetailsScreen && isAbCampaign) {
            const errorsObject = clone(errors) || {};
            errorsObject[CampaignErrorNames.WINNER_SELECTION_UNIT] = true;
            errorsObject[CampaignErrorNames.SETTING_AND_TRACKING] = true;
            dispatch(SetCampaignErrors(errorsObject));
        }
    }, [determineWinnerUnit, hasWinnerSelectionError]);

    const splitOptimizationOptions = [
        { value: SplitOptimization.Opened, label: t('byopens') },
        { value: SplitOptimization.Clicked, label: t('byclicks') },
    ];

    const handleChangeSplitOptimization = (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        obj: { value: SplitOptimization; label: string }
    ): void => {
        setSplitOptimization(obj.value);
        const modifyMap = new Map<string, any>();
        modifyMap.set('SplitOptions.SplitOptimization', obj.value);
        dispatch(ModifyCampaignMap(modifyMap));
    };

    const handleChangeDelayValue = (e: any): void => {
        const value = e.target.value ? e.target.value : e.currentTarget.value;
        const delay = getTimeInMinutes(value, determineWinnerUnit);
        const modifyMap = new Map<string, any>();
        modifyMap.set('SplitOptions.DetermineWinnerAfterXMinutes', delay);
        dispatch(ModifyCampaignMap(modifyMap));
    };

    const handleChangeUnitDelay = (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        obj: { value: TimeUnitsEnum; label: string }
    ): void => {
        const delay = getTimeInMinutes(determineWinnerValue, obj.value);
        const modifyMap = new Map<string, any>();
        modifyMap.set('SplitOptions.DetermineWinnerAfterXMinutes', delay);
        dispatch(ModifyCampaignMap(modifyMap));
        hasWinnerSelectionError && dispatch(VerifyCampaign());
    };

    if (!isAbCampaign) {
        return null;
    }

    const defaultValue =
        hasWinnerSelectionError && determineWinnerUnit === TimeUnitsEnum.Minutes ? TimeUnitsEnum.Minutes : unit;

    return (
        <WinnerSelectionWrapper>
            <Typography variant="h4" style={{ marginBottom: 2 }}>
                {t('howshouldthewinnerbechosen')}
            </Typography>
            <InputsWrapper>
                {isDetailsScreen ? (
                    <>
                        <BasicInput
                            defaultValue={getOptionLabel(splitOptimization, splitOptimizationOptions)}
                            readOnly
                        />
                        <BasicInput defaultValue={determineWinnerValue} readOnly />
                        <BasicInput
                            defaultValue={getOptionLabel(determineWinnerUnit, WinnerSelectionTimeOptions)}
                            readOnly
                        />
                    </>
                ) : (
                    <>
                        <SimplePopover
                            type="input"
                            options={splitOptimizationOptions}
                            defaultValue={SplitOptimizationEnum.Opened}
                            value={splitOptimization}
                            minWidth={300}
                            disableSentenceCase
                            onItemClick={handleChangeSplitOptimization}
                            listItemWidth={300}
                        />
                        <NumberInput
                            id="numberinput"
                            name="numberinput"
                            min={1}
                            defaultValue={value}
                            value={determineWinnerValue}
                            onClickButton={handleChangeDelayValue}
                            onChange={handleChangeDelayValue}
                        />
                        <SimplePopover
                            type="input"
                            options={WinnerSelectionTimeOptions}
                            defaultValue={defaultValue}
                            value={determineWinnerUnit}
                            defaultPlaceholder={defaultValue}
                            minWidth={101}
                            onItemClick={handleChangeUnitDelay}
                            error={hasWinnerSelectionError}
                        />
                    </>
                )}
                <Typography variant="body1">{t('aftersent')}</Typography>
            </InputsWrapper>
        </WinnerSelectionWrapper>
    );
};
